import { useEffect, useState } from "react";
import { FileSpreadsheet } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { FormField, inputClass, selectClass } from "../components/FormField";
import { fetchDecomptes, openDecompteSheet, type DecompteList } from "../api/invoicePrintApi";

function money(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function shortDate(value: string | null): string {
  return value ? new Date(`${value.slice(0, 10)}T00:00:00`).toLocaleDateString("fr-FR") : "—";
}

/**
 * Décompte : factures validées par le Sinistre, regroupées par entreprise et par lot de dépôt.
 * Chaque ligne ouvre la fiche que l'assureur envoie à l'entreprise pour payer les prestataires.
 */
export function DecomptePage() {
  const [company, setCompany] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [batch, setBatch] = useState("");
  const [data, setData] = useState<DecompteList | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (from && to && from > to) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchDecomptes({ company, from, to, batch })
        .then((list) => {
          if (!cancelled) setData(list);
        })
        .catch((err) => {
          if (!cancelled) setError(err instanceof Error ? err.message : "Décomptes indisponibles.");
        });
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [company, from, to, batch]);

  return (
    <>
      <Breadcrumbs items={[{ label: "Factures" }, { label: "Décompte" }]} />
      <div className="mb-6">
        <h1 className="text-xl font-bold text-foreground">Décompte</h1>
        <p className="text-sm text-muted">
          {data ? `${data.validated_invoices} facture${data.validated_invoices > 1 ? "s" : ""} validée${data.validated_invoices > 1 ? "s" : ""} par le Sinistre` : "Chargement…"}
        </p>
      </div>

      <div className="theme-card mb-5 grid grid-cols-1 gap-4 p-4 md:grid-cols-4">
        <FormField label="Entreprise">
          <select value={company} onChange={(e) => setCompany(e.target.value)} className={selectClass}>
            <option value="">Toutes les entreprises</option>
            {(data?.companies ?? []).map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </FormField>
        <FormField label="Du">
          <input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className={inputClass} />
        </FormField>
        <FormField label="Au">
          <input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className={inputClass} />
        </FormField>
        <FormField label="Lot">
          <select value={batch} onChange={(e) => setBatch(e.target.value)} className={selectClass}>
            <option value="">Tous les lots</option>
            {(data?.batches ?? []).map((name) => (
              <option key={name} value={name}>{name}</option>
            ))}
          </select>
        </FormField>
      </div>

      {error ? <p className="mb-4 text-sm text-red-600">{error}</p> : null}

      <div className="theme-card overflow-x-auto">
        <table className="safecheck-table w-full min-w-[900px] text-sm">
          <thead>
            <tr className="border-b border-border bg-card-muted/50 text-left text-[11px] font-semibold uppercase tracking-wide text-muted">
              <th className="px-4 py-3">Entreprise</th>
              <th className="px-4 py-3">Lot</th>
              <th className="px-4 py-3">Du</th>
              <th className="px-4 py-3">Au</th>
              <th className="px-4 py-3 text-right">Factures</th>
              <th className="px-4 py-3 text-right">Réclamé</th>
              <th className="px-4 py-3 text-right">Net à payer</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {!data ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted">Chargement…</td></tr>
            ) : data.rows.length === 0 ? (
              <tr><td colSpan={8} className="px-4 py-12 text-center text-muted">Aucune facture validée pour ces filtres.</td></tr>
            ) : (
              data.rows.map((row) => (
                <tr key={`${row.company}|${row.batch}`} className="border-b border-border last:border-0">
                  <td className="px-4 py-3 font-medium text-foreground">{row.company}</td>
                  <td className="px-4 py-3 text-foreground">{row.batch || "—"}</td>
                  <td className="px-4 py-3 tabular-nums text-foreground">{shortDate(row.from)}</td>
                  <td className="px-4 py-3 tabular-nums text-foreground">{shortDate(row.to)}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">{row.invoices}</td>
                  <td className="px-4 py-3 text-right tabular-nums text-foreground">{money(row.claimed)}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold text-foreground">{money(row.net)}</td>
                  <td className="px-4 py-3">
                    <button
                      type="button"
                      onClick={() => {
                        setError(null);
                        void openDecompteSheet({ company: row.company, batch: row.batch, from, to }).catch((err) =>
                          setError(err instanceof Error ? err.message : "Fiche de décompte indisponible."),
                        );
                      }}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground transition-colors hover:border-accent hover:bg-accent/20"
                    >
                      <FileSpreadsheet size={14} />
                      Voir le décompte
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </>
  );
}
